import { Users, ArrowRight } from 'lucide-react';
import { useT } from '@/i18n';

/**
 * Cluster H v2 Fase 2.7 — INBOUND referral card. Shown on ClientProfilePage
 * when ClientProfile.referredBy is not null. Peach palette (warm/welcoming —
 * "someone referred you to us") with a border-l-4 peach-400 accent.
 *
 * Different from PromotedBanner (Cluster H Fase Paralela Agent D):
 *   - PromotedBanner is a top-of-page status banner with a screening link.
 *   - ReferralChainCard is a clickable card with onParentClick(parentClientId)
 *     that opens the parent client in a ClientProfileModal overlay. Used
 *     for nested navigation without leaving the current page.
 *
 * The full PromotedBanner stays for users who want to jump to the parent
 * screening; this card adds the "open parent in modal" affordance.
 */

export interface ReferralChainCardProps {
  parentClientId: string;
  parentClientLastName: string;
  parentClientCinLast6: string;
  promotedAt: string;
  promotedByAgentName: string | null;
  /** Opens the parent client's profile in the ClientProfileModal overlay. */
  onParentClick: (parentClientId: string) => void;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ReferralChainCard({
  parentClientId,
  parentClientLastName,
  parentClientCinLast6,
  promotedAt,
  promotedByAgentName,
  onParentClick,
}: ReferralChainCardProps): JSX.Element {
  const t = useT();
  const parentLabel = parentClientCinLast6
    ? `${parentClientLastName} (CIN …${parentClientCinLast6})`
    : parentClientLastName;
  return (
    <div
      role="region"
      aria-label={t('referralChain.label')}
      className="rounded-md border border-peach-200 border-l-4 border-l-peach-400 bg-peach-50 p-3 flex items-start gap-3"
    >
      <Users className="h-5 w-5 text-peach-700 shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-peach-800">
          {t('referralChain.label')}
        </p>
        <p className="text-sm text-peach-900">
          {t('referralChain.fromHousehold', { name: '' })}{' '}
          <button
            type="button"
            onClick={() => onParentClick(parentClientId)}
            className="inline-flex items-center gap-1 font-semibold underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-peach-400 focus:ring-offset-1 rounded-sm transition-colors"
          >
            {parentLabel}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </p>
        <p className="text-xs text-peach-800">
          {fmtDate(promotedAt)}
          {promotedByAgentName ? ` · ${t('referralChain.promotedBy', { name: promotedByAgentName })}` : null}
        </p>
      </div>
    </div>
  );
}
